import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Group as GroupIcon,
  Message as MessageIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

interface Contact {
  id: string;
  name: string;
  phone: string;
  email?: string;
  source: string;
  crm_stages?: {
    id: string;
    name: string;
    crm_pipelines?: {
      id: string;
      name: string;
    };
  };
  created_at: string;
}

interface SmsCampaign {
  id?: string;
  name: string;
  description: string;
  message: string;
  channel: string;
  leads: Contact[];
  crm_stage_id?: string;
  scheduled_at?: string;
  settings: {
    enablePersonalization: boolean;
    enableTracking: boolean;
    enableRetry: boolean;
    maxRetries: number;
    delayBetweenMessages: number;
    sendInBatches: boolean;
    batchSize: number;
    batchDelay: number;
    senderName?: string;
    priority: 'low' | 'normal' | 'high';
  };
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'failed';
}



const Sms: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
  const [crmPipelines, setCrmPipelines] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const [campaign, setCampaign] = useState<SmsCampaign>({
    name: '',
    description: '',
    message: '',
    channel: 'sms',
    leads: [],
    settings: {
      enablePersonalization: true,
      enableTracking: true,
      enableRetry: true,
      maxRetries: 3,
      delayBetweenMessages: 1,
      sendInBatches: true,
      batchSize: 50,
      batchDelay: 5,
      priority: 'normal'
    },
    status: 'draft'
  });

  const [searchQuery, setSearchQuery] = useState('');

  const steps = [
    'Seleção de Destinatários',
    'Configuração da Mensagem',
    'Configurações Avançadas',
    'Revisão e Agendamento'
  ];

  // Carregar contatos e pipelines do CRM
  useEffect(() => {
    fetchContacts();
    fetchCrmPipelines();
  }, []);

    const fetchContacts = async () => {
    try {
      setLoading(true);
      // Usar a nova rota que busca todos os leads sem paginação
              const response = await axios.get(`${getApiUrl()}/crm/all-leads`);
      
      // Transformar os dados para o formato esperado
      const transformedContacts = (response.data.data || []).map((lead: any) => ({
        id: lead.contacts?.id || lead.id,
        name: lead.contacts?.name || 'Sem nome',
        phone: lead.contacts?.phone || lead.contacts?.custom_fields?.whatsapp_id || lead.contacts?.custom_fields?.telegram_id || '',
        email: lead.contacts?.email,
        source: lead.contacts?.source || 'CRM',
        crm_stages: lead.crm_stages ? {
          id: lead.crm_stages.id,
          name: lead.crm_stages.name,
          crm_pipelines: lead.crm_pipelines ? {
            id: lead.crm_pipelines.id,
            name: lead.crm_pipelines.name
          } : undefined
        } : undefined,
        created_at: lead.contacts?.created_at || lead.created_at
      }));
      
      setAvailableContacts(transformedContacts);
      console.log(`✅ Carregados ${transformedContacts.length} contatos para seleção`);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      showSnackbar('Erro ao carregar contatos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCrmPipelines = async () => {
    try {
              const response = await axios.get(`${getApiUrl()}/crm/pipelines`);
      
      // Transformar os dados para garantir que stages estejam disponíveis
      const transformedPipelines = (response.data.data || []).map((pipeline: any) => ({
        ...pipeline,
        stages: pipeline.stages || []
      }));
      
      setCrmPipelines(transformedPipelines);
    } catch (error) {
      console.error('Erro ao carregar pipelines:', error);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/campaigns/sms');
    } else {
    setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleContactSelection = (contact: Contact, selected: boolean) => {
    if (selected) {
      setCampaign(prev => ({
        ...prev,
        leads: [...prev.leads, contact]
      }));
    } else {
      setCampaign(prev => ({
        ...prev,
        leads: prev.leads.filter(c => c.id !== contact.id)
      }));
    }
  };

  const handleSelectAllContacts = () => {
    const filteredContacts = availableContacts.filter(contact => {
      const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           contact.phone.includes(searchQuery) ||
                           contact.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStage = !campaign.crm_stage_id || contact.crm_stages?.id === campaign.crm_stage_id;
      
      return matchesSearch && matchesStage;
    });

    setCampaign(prev => ({
      ...prev,
      leads: filteredContacts
    }));
  };

  const handleDeselectAllContacts = () => {
    setCampaign(prev => ({
      ...prev,
      leads: []
    }));
  };

  const handleImportPhones = () => {
    const phones = importText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(phone => {
        // Formatar telefone para padrão brasileiro
        const cleanPhone = phone.replace(/\D/g, '');
        if (cleanPhone.length === 11) {
          return `+55${cleanPhone}`;
        } else if (cleanPhone.length === 13 && cleanPhone.startsWith('55')) {
          return `+${cleanPhone}`;
        }
        return phone;
      });

    // Criar contatos virtuais para os telefones importados
    const virtualContacts = phones.map(phone => ({
      id: `virtual_${phone}`,
      name: `Contato ${phone}`,
      phone: phone,
      email: '',
      source: 'Importado',
      created_at: new Date().toISOString()
    }));

    setCampaign(prev => ({
      ...prev,
      leads: [...prev.leads, ...virtualContacts]
    }));

    setImportText('');
    setShowImportDialog(false);
    showSnackbar(`${phones.length} telefones importados com sucesso`, 'success');
  };

  const handleRemovePhone = (phone: string) => {
    setCampaign(prev => ({
      ...prev,
      leads: prev.leads.filter(c => c.phone !== phone)
    }));
  };

  const generatePreviewMessage = () => {
    let message = campaign.message;
    
    if (campaign.settings.enablePersonalization && campaign.leads.length > 0) {
      const sampleContact = campaign.leads[0];
      message = message
        .replace(/{nome}/g, sampleContact.name || 'Nome do Cliente')
        .replace(/{telefone}/g, sampleContact.phone || 'Telefone do Cliente')
        .replace(/{email}/g, sampleContact.email || 'Email do Cliente')
        .replace(/{canal}/g, sampleContact.source || 'Canal')
        .replace(/{estagio}/g, sampleContact.crm_stages?.name || 'Estágio');
    }
    
    return message;
  };

  const handleSaveCampaign = async () => {
    try {
      setSaving(true);
      
      // Validações
      if (!campaign.name || campaign.name.trim() === '') {
        alert('Por favor, informe o nome da campanha.');
        setSaving(false);
        return;
      }
      
      if (!campaign.message || campaign.message.trim() === '') {
        alert('Por favor, informe a mensagem da campanha.');
        setSaving(false);
        return;
      }
      
      if (campaign.leads.length === 0) {
        alert('Por favor, selecione pelo menos um destinatário para a campanha.');
        setSaving(false);
        return;
      }

      // Preparar dados para envio (sem leads inicialmente)
      const campaignData = {
        name: campaign.name.trim(),
        description: campaign.description.trim(),
        message: campaign.message.trim(),
        channel: campaign.channel,
        crm_stage_id: campaign.crm_stage_id || null,
        scheduled_at: campaign.scheduled_at || null,
        settings: campaign.settings,
        status: campaign.status
      };
      
      console.log('Dados da campanha sendo enviados:', campaignData);
      
      // Criar campanha primeiro
      const response = await axios.post('/api/v1/sms-campaigns', campaignData);
      console.log('Campanha salva:', response.data);
      
      // Adicionar leads separadamente
      if (campaign.leads.length > 0) {
        try {
          // Verificar se há IDs virtuais (contatos importados)
          const hasVirtualIds = campaign.leads.some(lead => lead.id.startsWith('virtual_'));
          
          if (hasVirtualIds) {
            // Se houver IDs virtuais (telefones digitados), enviar os dados completos dos leads com telefones
            console.log('Enviando leads com dados completos (telefones digitados):', campaign.leads.length);
            await axios.post(`/api/v1/sms-campaigns/${response.data.data.id}/leads`, {
              leads: campaign.leads.map(lead => ({
                phone: lead.phone,
                name: lead.name || `Contato ${lead.phone}`,
                email: lead.email || '',
                source: lead.source || 'Importado'
              }))
            });
          } else {
            // Se todos os IDs são válidos (contatos do sistema), enviar apenas os IDs
            const contactIds = campaign.leads.map(lead => lead.id).filter(id => id);
            if (contactIds.length > 0) {
              console.log('Adicionando leads do sistema à campanha:', contactIds);
          await axios.post(`/api/v1/sms-campaigns/${response.data.data.id}/leads`, {
            contactIds: contactIds
          });
            }
          }
          
          console.log('Leads adicionados com sucesso');
        } catch (leadsError: any) {
          console.error('Erro ao adicionar leads:', leadsError);
          showSnackbar(leadsError.response?.data?.error || 'Erro ao adicionar leads à campanha', 'error');
          // Não falhar se a adição de leads falhar
        }
      }
      
      showSnackbar('Campanha SMS criada com sucesso!', 'success');
      navigate('/campaigns/sms');
    } catch (error: any) {
      console.error('Erro ao salvar campanha:', error);
      console.error('Detalhes do erro:', error.response?.data);
      console.error('Status do erro:', error.response?.status);
      showSnackbar('Erro ao salvar campanha', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredContacts = availableContacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.phone.includes(searchQuery) ||
                         contact.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStage = !campaign.crm_stage_id || contact.crm_stages?.id === campaign.crm_stage_id;
    
    // Debug: log para verificar o filtro
    if (campaign.crm_stage_id && contact.crm_stages) {
      console.log('Filtro CRM:', {
        selectedStage: campaign.crm_stage_id,
        contactStage: contact.crm_stages.id,
        contactName: contact.name,
        matches: contact.crm_stages.id === campaign.crm_stage_id
      });
    }
    
    return matchesSearch && matchesStage;
  });



  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecionar Destinatários para SMS
            </Typography>
            
            {/* Filtros */}
            <Box className="glass-blue-card rounded-xl p-4 mb-4">
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                Filtros de Seleção
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: '#64748b' }}>Estágio do CRM</InputLabel>
                  <Select
                    value={campaign.crm_stage_id || ''}
                    onChange={(e) => setCampaign(prev => ({ ...prev, crm_stage_id: e.target.value }))}
                    label="Estágio do CRM"
                    sx={{
                      '& .MuiSelect-icon': { color: '#64748b' },
                      '& .MuiOutlinedInput-input': { color: '#000000' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                    }}
                  >
                    <MenuItem value="">Todos os estágios</MenuItem>
                    {crmPipelines.map(pipeline => 
                      pipeline.stages.map((stage: any) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          {pipeline.name} - {stage.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                placeholder="Buscar por nome, telefone ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& input': { color: '#000000' },
                    '& input::placeholder': { color: '#94a3b8' },
                    '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Box>

            {/* Importar Telefones */}
            <Box className="glass-blue-card rounded-xl p-4 mb-4">
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                Importar Lista de Telefones
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                Cole uma lista de telefones (um por linha) para importar
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => setShowImportDialog(true)}
                  sx={{
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                    '&:hover': {
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)'
                    }
                  }}
                >
                  Importar Telefones
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const phones = campaign.leads.map(lead => lead.phone).join('\n');
                    navigator.clipboard.writeText(phones);
                    showSnackbar('Telefones copiados para área de transferência', 'success');
                  }}
                  sx={{
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                    '&:hover': {
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)'
                    }
                  }}
                >
                  Exportar Lista
                </Button>
              </Box>
            </Box>

            {/* Ações em massa */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleSelectAllContacts}
                startIcon={<AddIcon />}
              >
                Selecionar Todos ({filteredContacts.length})
              </Button>
              <Button
                variant="outlined"
                onClick={handleDeselectAllContacts}
                startIcon={<RemoveIcon />}
              >
                Desmarcar Todos
              </Button>
            </Box>

            {/* Lista de contatos */}
            <Box className="glass-blue-card rounded-xl p-4 mb-4">
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                Contatos Disponíveis ({filteredContacts.length} de {availableContacts.length})
              </Typography>
              
              {/* Debug info */}
              {campaign.crm_stage_id && (
                <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: '#000000' }}>
                    <strong>Filtro ativo:</strong> Estágio do CRM selecionado
                    <br />
                    <strong>Total de contatos:</strong> {availableContacts.length}
                    <br />
                    <strong>Contatos com estágio:</strong> {availableContacts.filter(c => c.crm_stages).length}
                    <br />
                    <strong>Contatos filtrados:</strong> {filteredContacts.length}
                  </Typography>
                </Alert>
              )}
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {filteredContacts.map((contact) => {
                      const isSelected = campaign.leads.some(c => c.id === contact.id);
                      return (
                        <ListItem
                          key={contact.id}
                          sx={{
                            border: isSelected ? 2 : 1,
                            borderColor: isSelected ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                          }}
                        >
                          <ListItemText
                            primary={<Typography sx={{ color: '#000000' }}>{contact.name}</Typography>}
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                  📞 {contact.phone}
                                  {contact.email && ` | 📧 ${contact.email}`}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                  <Chip
                                    label={contact.source}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', color: '#475569' }}
                                  />
                                  {contact.crm_stages && (
                                    <Chip
                                      label={`${contact.crm_stages.crm_pipelines?.name} - ${contact.crm_stages.name}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', color: '#475569' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              variant={isSelected ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleContactSelection(contact, !isSelected)}
                              sx={isSelected ? {
                                backgroundColor: '#3b82f6',
                                color: '#ffffff',
                                '&:hover': { backgroundColor: '#2563eb' }
                              } : {
                                borderColor: 'rgba(59, 130, 246, 0.2)',
                                color: '#3b82f6',
                                '&:hover': {
                                  borderColor: 'rgba(59, 130, 246, 0.3)',
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)'
                                }
                              }}
                            >
                              {isSelected ? 'Selecionado' : 'Selecionar'}
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
            </Box>

            {/* Telefones importados */}
            {campaign.leads.filter(lead => lead.source === 'Importado').length > 0 && (
              <Box className="glass-blue-card rounded-xl p-4 mb-4">
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                  Telefones Importados ({campaign.leads.filter(lead => lead.source === 'Importado').length})
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {campaign.leads.filter(lead => lead.source === 'Importado').map((lead, index) => (
                    <Chip
                      key={index}
                      label={lead.phone}
                      onDelete={() => handleRemovePhone(lead.phone)}
                      deleteIcon={<DeleteIcon sx={{ color: '#64748b' }} />}
                      variant="outlined"
                      sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', color: '#475569' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                <strong>Total de destinatários:</strong> {campaign.leads.length}
              </Typography>
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
              Configurar Mensagem SMS
            </Typography>

            <Box className="glass-blue-card rounded-xl p-4 mb-4">
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                Informações da Campanha
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                <TextField
                  fullWidth
                  label="Nome da Campanha"
                  value={campaign.name}
                  onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Campanha Black Friday SMS"
                  sx={{ 
                    flex: 1, 
                    minWidth: 300,
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiOutlinedInput-root': {
                      '& input': { color: '#000000' },
                      '& input::placeholder': { color: '#94a3b8' },
                      '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Descrição"
                  value={campaign.description}
                  onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o objetivo da campanha"
                  sx={{ 
                    flex: 1, 
                    minWidth: 300,
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiOutlinedInput-root': {
                      '& input': { color: '#000000' },
                      '& input::placeholder': { color: '#94a3b8' },
                      '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                    }
                  }}
                />
              </Box>

              <TextField
                fullWidth
                label="Mensagem SMS"
                value={campaign.message}
                onChange={(e) => setCampaign(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Digite sua mensagem SMS aqui..."
                multiline
                rows={4}
                inputProps={{ maxLength: 160 }}
                helperText={`${campaign.message.length}/160 caracteres`}
                sx={{ 
                  mb: 2,
                  '& .MuiInputLabel-root': { color: '#64748b' },
                  '& .MuiOutlinedInput-root': {
                    '& textarea': { color: '#000000' },
                    '& textarea::placeholder': { color: '#94a3b8' },
                    '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                  },
                  '& .MuiFormHelperText-root': { color: '#64748b' }
                }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Variáveis disponíveis: {'{nome}, {telefone}, {email}, {canal}, {estagio}'}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={() => {
                  setPreviewMessage(generatePreviewMessage());
                  setShowPreview(true);
                }}
                startIcon={<MessageIcon />}
                sx={{
                  borderColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)'
                  }
                }}
              >
                Visualizar Mensagem
              </Button>
            </Box>

            <Box className="glass-blue-card rounded-xl p-4">
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                Resumo da Campanha
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<GroupIcon sx={{ color: '#3b82f6' }} />}
                  label={`${campaign.leads.length} destinatários`}
                  sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)' }}
                />
                <Chip
                  icon={<PhoneIcon sx={{ color: '#3b82f6' }} />}
                  label="SMS"
                  sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)' }}
                />
                {campaign.crm_stage_id && (
                  <Chip
                    label="Filtro por estágio aplicado"
                    variant="outlined"
                    sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', color: '#475569' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
              Configurações Avançadas
            </Typography>

            <Box className="glass-blue-card rounded-xl p-4 mb-4">
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
                Configurações de Envio
              </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                  <TextField
                    label="Nome do Remetente"
                    value={campaign.settings.senderName || ''}
                    onChange={(e) => setCampaign(prev => ({
                      ...prev,
                      settings: { ...prev.settings, senderName: e.target.value }
                    }))}
                    placeholder="Ex: Sua Empresa"
                    sx={{ 
                      minWidth: 200,
                      '& .MuiInputLabel-root': { color: '#64748b' },
                      '& .MuiOutlinedInput-root': {
                        '& input': { color: '#000000' },
                        '& input::placeholder': { color: '#94a3b8' },
                        '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                      }
                    }}
                  />
                  
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: '#64748b' }}>Prioridade</InputLabel>
                    <Select
                      value={campaign.settings.priority}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        settings: { ...prev.settings, priority: e.target.value as any }
                      }))}
                      label="Prioridade"
                      sx={{
                        '& .MuiSelect-icon': { color: '#64748b' },
                        '& .MuiOutlinedInput-input': { color: '#000000' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                      }}
                    >
                      <MenuItem value="low">Baixa</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">Alta</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Accordion sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}>
                    <Typography variant="subtitle2" sx={{ color: '#000000' }}>Personalização e Rastreamento</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.enablePersonalization}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, enablePersonalization: e.target.checked }
                            }))}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3b82f6' }
                            }}
                          />
                        }
                        label={<Typography sx={{ color: '#475569' }}>Habilitar personalização de mensagens</Typography>}
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.enableTracking}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, enableTracking: e.target.checked }
                            }))}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3b82f6' }
                            }}
                          />
                        }
                        label={<Typography sx={{ color: '#475569' }}>Rastrear entregas</Typography>}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Configurações de Retry</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.enableRetry}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, enableRetry: e.target.checked }
                            }))}
                          />
                        }
                        label="Tentar reenviar SMS falhados"
                      />
                      
                      <TextField
                        type="number"
                        label="Máximo de tentativas"
                        value={campaign.settings.maxRetries}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxRetries: parseInt(e.target.value) }
                        }))}
                        disabled={!campaign.settings.enableRetry}
                        sx={{ 
                          minWidth: 150,
                          '& .MuiInputLabel-root': { color: '#64748b' },
                          '& .MuiOutlinedInput-root': {
                            '& input': { color: '#000000' },
                            '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                          }
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Configurações de Envio</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <TextField
                        type="number"
                        label="Delay entre mensagens (segundos)"
                        value={campaign.settings.delayBetweenMessages}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, delayBetweenMessages: parseInt(e.target.value) }
                        }))}
                        sx={{ 
                          minWidth: 200,
                          '& .MuiInputLabel-root': { color: '#64748b' },
                          '& .MuiOutlinedInput-root': {
                            '& input': { color: '#000000' },
                            '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                          }
                        }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.sendInBatches}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, sendInBatches: e.target.checked }
                            }))}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3b82f6' }
                            }}
                          />
                        }
                        label={<Typography sx={{ color: '#475569' }}>Enviar em lotes</Typography>}
                      />
                      
                      {campaign.settings.sendInBatches && (
                        <>
                          <TextField
                            type="number"
                            label="Tamanho do lote"
                            value={campaign.settings.batchSize}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, batchSize: parseInt(e.target.value) }
                            }))}
                            sx={{ 
                              minWidth: 150,
                              '& .MuiInputLabel-root': { color: '#64748b' },
                              '& .MuiOutlinedInput-root': {
                                '& input': { color: '#000000' },
                                '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                              }
                            }}
                          />
                          
                          <TextField
                            type="number"
                            label="Delay entre lotes (minutos)"
                            value={campaign.settings.batchDelay}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, batchDelay: parseInt(e.target.value) }
                            }))}
                            sx={{ 
                              minWidth: 150,
                              '& .MuiInputLabel-root': { color: '#64748b' },
                              '& .MuiOutlinedInput-root': {
                                '& input': { color: '#000000' },
                                '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
                              }
                            }}
                          />
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
            </Box>

            <Alert severity="warning" sx={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                <strong>Atenção:</strong> Respeite os limites de taxa dos provedores SMS para evitar bloqueios.
              </Typography>
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisar e Agendar
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informações da Campanha
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2">
                    <strong>Nome:</strong> {campaign.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Descrição:</strong> {campaign.description}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Destinatários:</strong> {campaign.leads.length}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Remetente:</strong> {campaign.settings.senderName || 'Padrão'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Prioridade:</strong> {campaign.settings.priority}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Configurações
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2">
                    <strong>Personalização:</strong> {campaign.settings.enablePersonalization ? 'Sim' : 'Não'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Rastreamento:</strong> {campaign.settings.enableTracking ? 'Sim' : 'Não'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Retry:</strong> {campaign.settings.enableRetry ? `${campaign.settings.maxRetries} tentativas` : 'Não'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Delay:</strong> {campaign.settings.delayBetweenMessages}s entre mensagens
                  </Typography>
                  <Typography variant="body2">
                    <strong>Lotes:</strong> {campaign.settings.sendInBatches ? `${campaign.settings.batchSize} por lote` : 'Não'}
                  </Typography>
                </Paper>
              </Box>
            </Box>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Mensagem
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {generatePreviewMessage()}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Estimativa:</strong> Esta campanha enviará {campaign.leads.length} SMS.
                {campaign.settings.sendInBatches && 
                                      ` Serão enviados em ${Math.ceil(campaign.leads.length / campaign.settings.batchSize)} lotes.`
                }
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/campaigns')} sx={{ mr: 2, color: '#475569' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ color: '#000000', fontWeight: 'bold' }}>
          Campanha de SMS em Massa
        </Typography>
      </Box>

      {/* Stepper */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6">
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ 
                '& .MuiStepLabel-label': { 
                  color: '#475569',
                  '&.Mui-active': { color: '#3b82f6' },
                  '&.Mui-completed': { color: '#3b82f6' }
                }
              }}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Conteúdo do Step */}
      <Box className="glass-blue-card rounded-xl p-4">
        {renderStepContent()}
      </Box>

      {/* Ações */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: 'rgba(59, 130, 246, 0.2)',
            color: '#3b82f6',
            '&:hover': {
              borderColor: 'rgba(59, 130, 246, 0.3)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)'
            },
            '&:disabled': {
              borderColor: 'rgba(59, 130, 246, 0.1)',
              color: '#94a3b8'
            }
          }}
          variant="outlined"
        >
          Voltar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setCampaign(prev => ({ ...prev, status: 'draft' }))}
                disabled={saving}
                sx={{
                  borderColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)'
                  }
                }}
              >
                Salvar como Rascunho
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveCampaign}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
                  }
                }}
              >
                {saving ? 'Salvando...' : 'Agendar Campanha'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && campaign.leads.length === 0) ||
                (activeStep === 1 && (!campaign.name || !campaign.message))
              }
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
                }
              }}
            >
              Próximo
            </Button>
          )}
        </Box>
      </Box>

      {/* Dialog de Preview */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: 'glass-blue-card',
          sx: {
            '&:hover': { background: 'inherit', borderColor: 'inherit', boxShadow: 'inherit', transform: 'none' }
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000' }}>Visualização da Mensagem SMS</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', p: 2, bgcolor: 'rgba(59, 130, 246, 0.08)', borderRadius: 1, color: '#000000' }}>
            {previewMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
          <Button onClick={() => setShowPreview(false)} sx={{ color: '#3b82f6' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Importação */}
      <Dialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: 'glass-blue-card',
          sx: {
            '&:hover': { background: 'inherit', borderColor: 'inherit', boxShadow: 'inherit', transform: 'none' }
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000' }}>Importar Lista de Telefones</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Cole uma lista de telefones, um por linha. Os telefones serão formatados automaticamente.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="+5511999999999&#10;+5511888888888&#10;11977777777&#10;..."
            sx={{
              '& .MuiOutlinedInput-root': {
                '& textarea': { color: '#000000' },
                '& textarea::placeholder': { color: '#94a3b8' },
                '& fieldset': { borderColor: 'rgba(59, 130, 246, 0.2)' }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
          <Button onClick={() => setShowImportDialog(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleImportPhones} variant="contained" sx={{ backgroundColor: '#3b82f6', '&:hover': { backgroundColor: '#2563eb' } }}>
            Importar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Sms; 